/**
 * PageEditor — TipTap-backed notebook page body.
 *
 * Replaces the Phase-2 textarea stub. The DB still stores `body_md`
 * (markdown); the editor is presentation. Round-trip is handled by
 * `tiptap-markdown`; the custom PageLink / Mention nodes register their
 * own `addStorage().markdown.serialize/parse` hooks.
 *
 * Save strategy:
 *   - Every `onUpdate` sets a `dirty` flag.
 *   - A 750 ms debounce flushes the body via PATCH `/pages/:id?version=N`.
 *   - On unmount or page change, any pending save is awaited.
 *
 * Sync model (the reason typing doesn't get kicked out of edit mode):
 *   - We track `lastSyncedVersion` — the server version the editor's
 *     document currently reflects.
 *   - After our own save, `lastSyncedVersion` advances to the server's
 *     response version. The post-save refetch in `usePage` then delivers
 *     a `page` prop with the SAME version → the merge effect is a no-op.
 *   - Remote updates (realtime, other-tab) arrive with a HIGHER version
 *     → we accept the server's body, but only when the user is idle.
 *   - Page navigation (pageId change) seeds the editor from the new page.
 *
 * Editor lifecycle (the OTHER reason typing doesn't get kicked out):
 *   - The editor is created ONCE (deps = [page.id, wsId]) and never
 *     re-mounted on data refetches. The dynamic data (pageTree,
 *     members) is passed to extensions via **refs** so they read the
 *     latest values lazily without invalidating the extensions array.
 *     This is what kept the cursor alive across auto-saves.
 *
 * UX:
 *   - NotebookBubbleMenu — Notion-style floating toolbar on selection.
 *   - NotebookContextMenu — Notion-style right-click menu.
 *   - NotebookBlockHandle — hover `+` button to insert a new block.
 *
 * Styling for the chips lives in `styles/index.css` (`.page-link-chip`,
 * `.mention-chip`) so the editor and the read-only views share one
 * stylesheet.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import { useNavigate } from '@tanstack/react-router'
import type { Page } from '@friday/shared/realtime'
import { useUpdatePage, usePageTree } from '@/features/notebook/pages'
import { useWorkspaceMembers } from '@/features/workspaces/workspaces'
import { useProfiles, pickDisplay } from '@/features/profiles/profiles'
import { useAuth } from '@/features/auth/authContext'
import { getExtensions, type MemberDirectoryEntry } from '@/lib/tiptap/extensions'
import { cn } from '@/lib/utils'
import { NotebookBubbleMenu } from './NotebookBubbleMenu'
import { NotebookContextMenu } from './NotebookContextMenu'
import { NotebookBlockHandle } from './NotebookBlockHandle'
import { NotebookTableControls } from './NotebookTableControls'

type SaveState = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

export interface PageEditorProps {
  wsId: string
  page: Page
}

export function PageEditor({ wsId, page }: PageEditorProps) {
  const [state, setState] = useState<SaveState>('idle')
  const update = useUpdatePage(wsId)
  const tree = usePageTree(wsId)
  const membersQ = useWorkspaceMembers(wsId)
  const { user } = useAuth()
  const memberIds = useMemo(() => (membersQ.data ?? []).map((m) => m.userId), [membersQ.data])
  const profiles = useProfiles(memberIds)

  // Refs that the extensions read at suggestion / parse time. Kept up to
  // date in an effect so we don't trigger editor re-mounts on data
  // refetches (see the lifecycle note at the top of the file).
  const pageTreeRef = useRef<Page[]>([])
  const memberDirectoryRef = useRef<MemberDirectoryEntry[]>([])

  useEffect(() => {
    pageTreeRef.current = tree.data ?? []
  }, [tree.data])
  useEffect(() => {
    memberDirectoryRef.current = (membersQ.data ?? []).map((m) => {
      const p = profiles.profiles[m.userId]
      const d = pickDisplay(m.userId, p)
      return { userId: m.userId, displayName: d.displayName, email: d.email }
    })
  }, [membersQ.data, profiles.profiles])

  // Build the extension list ONCE. The page tree and member directory
  // come in via refs (see getExtensions). Adding `tree.data` or
  // `membersQ.data` here would re-mount the editor on every refetch.
  const extensions = useMemo(
    () =>
      getExtensions({
        wsId,
        pageTree: pageTreeRef,
        members: memberDirectoryRef,
        currentUserId: user?.id,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wsId],
  )

  // Sync state: the server version the editor's document currently
  // reflects. After our own save, we bump this to the response version
  // so the post-save refetch of `usePage` doesn't trigger a redundant
  // `setContent` (which would clobber the user's cursor and selection).
  const lastSyncedVersion = useRef<number>(page.version)
  const lastSyncedBodyMd = useRef<string>(page.bodyMd ?? '')
  const activePageId = useRef<string>(page.id)
  const [contextAnchor, setContextAnchor] = useState<{ x: number; y: number } | null>(null)

  // CRITICAL: only re-mount the editor when the page changes (or the
  // workspace). The editor instance is stable across all other renders.
  const editor = useEditor(
    {
      extensions,
      content: page.bodyMd ?? '',
      autofocus: false,
      onUpdate: ({ editor: ed }) => {
        const md = (ed.storage.markdown?.getMarkdown() ?? '') as string
        setState(md === lastSyncedBodyMd.current ? 'idle' : 'dirty')
      },
    },
    [page.id, wsId],
  )

  // Merge incoming `page` prop into the editor.
  //
  // Three cases:
  //   1. Navigation: `page.id` changed → seed from new page.
  //   2. Own save response: same `page.id`, same `page.version` as
  //      `lastSyncedVersion` → no-op (we already wrote it).
  //   3. Remote update: same `page.id`, `page.version` GREATER than
  //      `lastSyncedVersion` → accept the server's body, but only when
  //      the user is not mid-edit.
  useEffect(() => {
    if (!editor) return

    // 1. Navigation.
    if (page.id !== activePageId.current) {
      activePageId.current = page.id
      lastSyncedVersion.current = page.version
      lastSyncedBodyMd.current = page.bodyMd ?? ''
      editor.commands.setContent(page.bodyMd ?? '', false)
      setState('idle')
      return
    }

    // 2. Own save response (version unchanged or already accounted for).
    if (page.version <= lastSyncedVersion.current) return

    // 3. Remote update — accept unless the user is mid-edit.
    if (state === 'dirty' || state === 'saving') return
    lastSyncedVersion.current = page.version
    lastSyncedBodyMd.current = page.bodyMd ?? ''
    editor.commands.setContent(page.bodyMd ?? '', false)
    setState('idle')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page.id, page.version, page.bodyMd, editor])

  // Right-click → open Notion context menu. Suppress the browser menu.
  useEffect(() => {
    const root = editor?.view?.dom as HTMLElement | undefined
    if (!root) return
    const onContext = (e: MouseEvent) => {
      e.preventDefault()
      setContextAnchor({ x: e.clientX, y: e.clientY })
    }
    root.addEventListener('contextmenu', onContext)
    return () => root.removeEventListener('contextmenu', onContext)
  }, [editor])

  const flush = useCallback(async () => {
    if (!editor) return
    const md = (editor.storage.markdown?.getMarkdown() ?? '') as string
    if (md === lastSyncedBodyMd.current) {
      setState('idle')
      return
    }
    setState('saving')
    try {
      const updated = await update.mutateAsync({ pageId: page.id, version: page.version, patch: { bodyMd: md } })
      lastSyncedBodyMd.current = updated.bodyMd ?? md
      // CRITICAL: bump lastSyncedVersion to the response version so the
      // post-save refetch of `usePage` doesn't re-setContent over the
      // user's still-blurring cursor.
      lastSyncedVersion.current = updated.version
      setState('saved')
      setTimeout(() => {
        setState((s) => (s === 'saved' ? 'idle' : s))
      }, 1500)
    } catch {
      setState('error')
    }
  }, [editor, update, page.id, page.version])

  // Flush any pending save when the user clicks out of the editor. The
  // 30 s debounce is too slow if the user navigates away or closes the
  // tab — the blur handler makes the save effectively immediate while
  // still keeping the version-noise low during normal typing.
  useEffect(() => {
    const root = editor?.view?.dom as HTMLElement | undefined
    if (!root) return
    const onBlur = () => {
      if (state === 'dirty') void flush()
    }
    // Use capture so we fire before any focus-stealing child element
    // (e.g. a button in a portal) decides what to do.
    root.addEventListener('blur', onBlur, true)
    return () => root.removeEventListener('blur', onBlur, true)
  }, [editor, state, flush])

  // Debounced auto-save. We wait 30 s of inactivity before flushing
  // because every save increments `version` (the optimistic-concurrency
  // token the server stamps on PATCH). Saving on every keystroke pause
  // — as a 750 ms debounce does — produces dozens of versions per
  // paragraph and floods the activity feed. 30 s is a good balance
  // between data safety and version noise. The user can always click
  // Save to flush immediately, and the unmount/cleanup handler saves
  // any pending dirty state before the page unmounts.
  useEffect(() => {
    if (!editor) return
    if (state !== 'dirty') return
    const handle = setTimeout(() => {
      void flush()
    }, 30_000)
    return () => clearTimeout(handle)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, editor])

  // Flush before unmount / page change.
  useEffect(() => {
    return () => {
      if (state === 'dirty') void flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Click-to-navigate for page-link chips. The editor renders <a href="#/page/...">;
  // we intercept the click and use TanStack Router so it stays SPA.
  const navigate = useNavigate()
  const editorRootRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const root = editorRootRef.current
    if (!root) return
    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement)?.closest('a[data-page-link]') as HTMLAnchorElement | null
      if (!target) return
      const id = target.getAttribute('data-page-id')
      if (!id) return
      e.preventDefault()
      navigate({ to: '/w/$wsId/n/$pageId', params: { wsId, pageId: id } })
    }
    root.addEventListener('click', onClick)
    return () => root.removeEventListener('click', onClick)
  }, [navigate, wsId, editor])

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-surface-200 px-6 py-2 text-xs text-surface-500 dark:border-surface-800">
        <span>
          v{page.version} · /{page.slug}
        </span>
        <SaveBadge state={state} onManualSave={() => void flush()} />
      </div>
      <div ref={editorRootRef} className="notebook-editor relative min-h-0 flex-1 overflow-auto">
        <EditorContent editor={editor} className="px-12 py-10 outline-none" />
        <NotebookBlockHandle editor={editor} />
        <NotebookBubbleMenu editor={editor} />
        <NotebookTableControls editor={editor} containerRef={editorRootRef} />
        <NotebookContextMenu
          editor={editor}
          anchor={contextAnchor}
          onClose={() => setContextAnchor(null)}
        />
      </div>
    </div>
  )
}

function SaveBadge({ state, onManualSave }: { state: SaveState; onManualSave: () => void }) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          'rounded px-2 py-0.5 text-[10px] uppercase tracking-wider transition-colors',
          state === 'dirty' && 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200',
          state === 'saving' && 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200',
          state === 'saved' && 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200',
          state === 'error' && 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-200',
          state === 'idle' && 'text-surface-500',
        )}
      >
        {state === 'idle' && 'Synced'}
        {state === 'dirty' && 'Unsaved'}
        {state === 'saving' && 'Saving…'}
        {state === 'saved' && 'Saved'}
        {state === 'error' && 'Save failed'}
      </span>
      <button
        type="button"
        onClick={onManualSave}
        disabled={state === 'idle' || state === 'saving'}
        className="btn-primary"
        title="Save now"
      >
        Save
      </button>
    </div>
  )
}
